// Player commands: the only way a player changes the game. The server runs these for online play and the
// local runner runs them for solo play. Every command is validated here; nothing from a client is trusted.
import {
  G, CFG, S, stationsOf, emit, claimRegion, netChanged, createLine, removeLine, takeTrain, addCarriage, upgradeable,
  snapToLine, validSpot, buildDepot, insertIntoLine, depotCost, upgradeCost, releaseSurge, surgeReady, annex, ruinsNextTo,
  eliminate, hops,
} from './game.js';

const notice = (p, text) => emit({ type: 'notice', to: p.id, text });
const isId = v => Number.isInteger(v) && v >= 0;
const isNum = v => typeof v === 'number' && Number.isFinite(v);
const own = (p, id) => { const s = isId(id) && S(id); return s && s.owner === p.id ? s : null; };
const ownLine = (p, id) => p.lines.find(L => L.id === id) || null;

const HANDLERS = {
  // draft: claim a starting region
  claim(p, c, seat) { return isId(c.rid) && claimRegion(seat, c.rid); },

  // drag a line from station a to station b: extend a line that ends at a, close a loop, or start a new line
  link(p, c) {
    const a = own(p, c.a), b = own(p, c.b);
    if (!a || !b || a === b) return false;
    const ends = p.lines.filter(L => !L.loop && (L.st[0] === a.id || L.st[L.st.length - 1] === a.id));
    const prefer = ownLine(p, c.line) || ownLine(p, p.lastLine);
    const L = (prefer && ends.includes(prefer) ? prefer : null) || ends[0];
    if (L) {
      const first = L.st[0], last = L.st[L.st.length - 1];
      if (!L.st.includes(b.id)) {
        if (last === a.id) L.st.push(b.id);
        else { L.st.unshift(b.id); for (const T of L.trains) { T.idx++; if (T.to != null) T.to++; } }
        netChanged(p); p.lastLine = L.id;
        emit({ type: 'linked', to: p.id, line: L.id });
        return true;
      }
      if (L.st.length >= 3 && ((first === a.id && last === b.id) || (last === a.id && first === b.id))) {
        L.loop = true; L.trains.forEach((T, i) => { T.dir = i % 2 ? -1 : 1; });
        netChanged(p); notice(p, 'Loop closed.');
        return true;
      }
    }
    if (p.lines.length >= p.lineLimit) { notice(p, 'No free lines. Remove one, or buy another.'); return false; }
    const NL = createLine(p, [a.id, b.id]);
    p.lastLine = NL.id;
    emit({ type: 'linked', to: p.id, line: NL.id });
    return true;
  },
  endDrag(p) { p.lastLine = null; return true; },
  removeLine(p, c) {
    const L = ownLine(p, c.line); if (!L) return false;
    const kept = removeLine(p, L);
    notice(p, kept ? `Line removed. ${kept} train${kept > 1 ? 's' : ''} kept as spare${kept > 1 ? 's' : ''}: + Train uses ${kept > 1 ? 'them' : 'it'} first, free.` : 'Line removed.');
    return true;
  },

  // depots and recyclers: dropped onto a stretch of line joins that line
  build(p, c) {
    if (c.kind !== 'depot' && c.kind !== 'recycler') return false;
    if (!G.players.some(q => q !== p && q.shape === c.shape) || !isNum(c.x) || !isNum(c.y)) return false;
    const pt = { x: c.x, y: c.y }, snap = snapToLine(p, pt), spot = snap ? snap.pos : pt;
    if (!validSpot(p, spot)) { notice(p, snap ? 'Not enough room on that stretch of line.' : 'Too close to the border or another station.'); return false; }
    const d = buildDepot(p, c.shape, spot, c.kind);
    if (!d) return false;
    if (snap) { insertIntoLine(p, snap.L, snap.i, d); notice(p, `${c.kind === 'recycler' ? 'Recycler' : 'Depot'} added to the line.`); }
    return true;
  },
  upgradeStation(p, c) {
    const s = own(p, c.station), cost = c.kind === 'warehouse' ? CFG.warehouseCost : c.kind === 'customs' ? CFG.customsCost : null;
    if (!s || cost == null || s[c.kind] || p.coins < cost) return false;
    if (c.kind === 'customs' && s.kind !== 'gate') return false;
    p.coins -= cost; s[c.kind] = true;
    notice(p, c.kind === 'customs' ? 'Customs post open: 3x toll, rivals cross slowly.' : 'Warehouse built: +10 platform space.');
    return true;
  },

  addTrain(p, c) {
    const L = ownLine(p, c.line) || p.lines.slice().sort((x, y) => x.trains.length - y.trains.length)[0];
    const how = L && takeTrain(p, L);
    if (!how) return false;
    notice(p, (how === 'spare' ? `Spare train back in service (${L.trains[L.trains.length - 1].cap} seats)` : 'Train added') + (L.loop ? ', running the other way round.' : '.'));
    return true;
  },
  addCarriage(p, c) {
    const L = ownLine(p, c.line);
    if (!L) { notice(p, 'Click a line first, then add a carriage to it.'); return false; }
    if (!upgradeable(L).length) return false;
    const T = addCarriage(p, L);
    if (T) notice(p, `Carriage added. That train now seats ${T.cap}.`);
    return !!T;
  },
  buyLine(p) {
    if (p.coins < CFG.lineCost || p.lineLimit >= CFG.lineMax) return false;
    p.coins -= CFG.lineCost; p.lineLimit++;
    return true;
  },

  target(p, c) {
    if (c.v === 'auto') { p.target = 'auto'; return true; }
    const q = isId(c.v) && G.players[c.v];
    if (!q || q === p || !q.alive || hops(p.region, q.region) == null) return false;
    p.target = q.id; return true;
  },
  rate(p, c) {
    if (c.delta !== 1 && c.delta !== -1) return false;
    p.rate = Math.max(1, Math.min(p.rateMax, p.rate + c.delta));
    return true;
  },
  upgradeRate(p) {
    if (p.rateMax >= CFG.rateCap || p.coins < upgradeCost(p)) return false;
    p.coins -= upgradeCost(p); p.rateMax++; p.rate = p.rateMax;
    return true;
  },
  bank(p) { p.banking = !p.banking; return true; },
  surge(p) {
    if (!surgeReady(p)) { notice(p, p.bank < CFG.surgeMin ? `A surge needs at least ${CFG.surgeMin} banked shapes.` : 'Your next surge is not ready yet.'); return false; }
    if (!releaseSurge(p)) { notice(p, 'No rival to send a surge to right now.'); return false; }
    return true;
  },
  annex(p, c) { return isId(c.rid) && ruinsNextTo(p).some(r => r.id === c.rid) && annex(p, c.rid); },

  // testing helpers, only in rooms created with cheats on
  cheat(p, c) {
    if (!G.cheats) return false;
    if (c.what === 'coins' && (c.n === 100 || c.n === 1000)) { p.coins += c.n; return true; }
    if (c.what === 'rush') { G.time = Math.max(G.time, CFG.rushAt); return true; }
    if (c.what === 'kill') {
      const rivals = G.players.filter(q => q.alive && q !== p);
      if (!rivals.length) return false;
      rivals.sort((a, b) => (hops(p.region, a.region) ?? 99) - (hops(p.region, b.region) ?? 99));
      const v = rivals[0], st = stationsOf(v)[0];
      if (st) st.units.push({ id: G.uid++, shape: p.shape, sender: p.id, target: v.id });   // credit the kill
      eliminate(v);
      return true;
    }
    return false;
  },
};
export const COMMAND_TYPES = Object.keys(HANDLERS);

// returns true when the command changed the game
export function applyCommand(seat, cmd) {
  // own properties only: '__proto__' or 'constructor' must never reach a handler lookup
  if (!cmd || typeof cmd !== 'object' || typeof cmd.type !== 'string' || !Object.hasOwn(HANDLERS, cmd.type)) return false;
  if (cmd.type === 'claim') return G.phase === 'draft' && HANDLERS.claim(null, cmd, seat);
  if (G.phase !== 'play') return false;
  const p = G.players[seat];
  if (!p || !p.alive) return false;
  return !!HANDLERS[cmd.type](p, cmd);
}
